import { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

export default function AdminContacts() {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchContacts = async () => {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      try {
        const response = await axios.get(`${BACKEND_URL}/api/contacts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, page_size: PAGE_SIZE },
        });
        if (!cancelled) setData(response.data);
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchContacts();
    return () => { cancelled = true; };
  }, [page]);

  const contacts = data.items;

  return (
    <div data-testid="admin-contacts">
      <h1 className="text-4xl font-unbounded font-black mb-8">Contact Messages</h1>

      <div className="bg-card p-6 border border-border mb-6">
        <p className="text-sm font-manrope text-gray-400">Total Messages</p>
        <p className="text-3xl font-unbounded font-black mt-2">{data.total}</p>
      </div>

      {loading ? (
        <p className="text-center font-manrope">Loading contacts...</p>
      ) : contacts.length === 0 ? (
        <div className="bg-card p-12 border border-border text-center">
          <p className="font-manrope text-gray-400">No contact messages yet.</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {contacts.map((contact) => (
              <div key={contact.id} className="bg-card p-6 border border-border" data-testid={`contact-${contact.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-unbounded font-bold">{contact.name}</h3>
                    <a href={`mailto:${contact.email}`} className="font-manrope text-accent hover:underline flex items-center gap-2">
                      <Mail size={16} />
                      {contact.email}
                    </a>
                  </div>
                  <p className="font-manrope text-sm text-gray-400">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="text-sm font-manrope text-gray-400 mb-1">Subject</p>
                  <p className="font-manrope font-medium">{contact.subject}</p>
                </div>
                <div>
                  <p className="text-sm font-manrope text-gray-400 mb-2">Message</p>
                  <p className="font-manrope">{contact.message}</p>
                </div>
              </div>
            ))}
          </div>
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6" data-testid="pagination">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="flex items-center gap-1 px-4 py-2 border border-border font-manrope disabled:opacity-40" data-testid="pagination-prev">
                <ChevronLeft size={16} /> Prev
              </button>
              <span className="font-manrope text-gray-400">Page {page} of {data.total_pages}</span>
              <button onClick={() => setPage(Math.min(data.total_pages, page + 1))} disabled={page >= data.total_pages} className="flex items-center gap-1 px-4 py-2 border border-border font-manrope disabled:opacity-40" data-testid="pagination-next">
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
